/**
 * 网络配置
 * 集中管理所有网络请求相关的配置、拦截器和工具
 * 包含统一的端口配置管理
 */

// 声明 process 环境变量类型
declare const process: {
  env: {
    NODE_ENV?: string;
    REACT_APP_API_URL?: string;
    REACT_APP_API_PREFIX?: string;
    REACT_APP_TIMEOUT?: string;
    REACT_APP_DEBUG?: string;
    REACT_APP_MSADMIN_PORT?: string;
    REACT_APP_MSLOGIN_PORT?: string;
    REACT_APP_MSLOGIN_API_PORT?: string;
    /** 角色等接口所在 msLogin 的 API 根（如 https://login.ihoms.cn 或 .../api）；不配则用 UNIFIED_API_BASE */
    REACT_APP_MSLOGIN_API_URL?: string;
    REACT_APP_MSSPACE_PORT?: string;
    REACT_APP_MSSTUN_PORT?: string;
    REACT_APP_MSMQ_PORT?: string;
    REACT_APP_MSLOG_PORT?: string;
    REACT_APP_MSMAIN_PORT?: string;
  };
};

// 确保process对象存在
if (typeof process === 'undefined') {
  (window as any).process = {
    env: {
      NODE_ENV: 'development',
      REACT_APP_API_URL: '',
      REACT_APP_API_PREFIX: '',
      REACT_APP_TIMEOUT: '15000',
      REACT_APP_DEBUG: 'false',
      REACT_APP_MSADMIN_PORT: '30091',
      REACT_APP_MSLOGIN_PORT: '30090',
      REACT_APP_MSLOGIN_API_PORT: '50090',
      REACT_APP_MSSPACE_PORT: '30092',
      REACT_APP_MSSTUN_PORT: '30093',
      REACT_APP_MSMQ_PORT: '30094',
      REACT_APP_MSLOG_PORT: '30095',
      REACT_APP_MSMAIN_PORT: '3001',
    }
  };
}

// 安全获取环境变量的辅助函数
// 支持构建时和运行时环境变量
const getEnvVar = (key: string, defaultValue: string = ''): string => {
  try {
    // 1. 优先使用构建时的环境变量（process.env，在构建时会被替换）
    if (typeof process !== 'undefined' && process.env && process.env[key]) {
      return process.env[key];
    }
    // 2. 使用 window.process.env（运行时注入的环境变量）
    if (typeof window !== 'undefined' && (window as any).process?.env?.[key]) {
      return (window as any).process.env[key];
    }
    // 3. 使用默认值
    return defaultValue;
  } catch (error) {
    return defaultValue;
  }
};
import axios, { AxiosInstance, AxiosRequestConfig, AxiosError, AxiosHeaders } from "axios";
import { message } from "antd";
import { notifyAuthFailed } from "../utils/parentCommunication";

// ===== 端口配置 =====

// 微服务端口配置接口
interface ServicePorts {
  // 前端端口
  msAdmin: number;
  msLogin: number;
  msSpace: number;
  msStun: number;
  msMq: number;
  msLog: number;
  msMain: number;

  // 后端API端口
  msAdminApi: number;
  msLoginApi: number;
  msSpaceApi: number;
  msStunApi: number;
  msMqApi: number;
  msLogApi: number;
}

// 不同环境的端口配置
const portConfigs: Record<string, ServicePorts> = {
  development: {
    // 前端端口
    msAdmin: parseInt(getEnvVar('REACT_APP_MSADMIN_PORT', '30091'), 10),
    msLogin: parseInt(getEnvVar('REACT_APP_MSLOGIN_PORT', '30090'), 10),
    msSpace: parseInt(getEnvVar('REACT_APP_MSSPACE_PORT', '30092'), 10),
    msStun: parseInt(getEnvVar('REACT_APP_MSSTUN_PORT', '30093'), 10),
    msMq: parseInt(getEnvVar('REACT_APP_MSMQ_PORT', '30094'), 10),
    msLog: parseInt(getEnvVar('REACT_APP_MSLOG_PORT', '30095'), 10),
    msMain: parseInt(getEnvVar('REACT_APP_MSMAIN_PORT', '3001'), 10),

    // 后端API端口
    msAdminApi: 50091,
    msLoginApi: parseInt(getEnvVar('REACT_APP_MSLOGIN_API_PORT', '50090'), 10),
    msSpaceApi: 50092,
    msStunApi: 50093,
    msMqApi: 50094,
    msLogApi: 50095,
  },
  test: {
    // 前端端口
    msAdmin: 30091,
    msLogin: 30090,
    msSpace: 30092,
    msStun: 30093,
    msMq: 30094,
    msLog: 30095,
    msMain: 3001,

    // 后端API端口
    msAdminApi: 50091,
    msLoginApi: 50090,
    msSpaceApi: 50092,
    msStunApi: 50093,
    msMqApi: 50094,
    msLogApi: 50095,
  },
  production: {
    // 生产环境使用标准端口
    msAdmin: 80,
    msLogin: 80,
    msSpace: 80,
    msStun: 80,
    msMq: 80,
    msLog: 80,
    msMain: 80,

    // 后端API端口
    msAdminApi: 443,
    msLoginApi: 443,
    msSpaceApi: 443,
    msStunApi: 443,
    msMqApi: 443,
    msLogApi: 443,
  },
};

// 确定当前环境
// 优先使用构建时的 NODE_ENV，如果没有则使用运行时环境变量，最后默认为 development
const env = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV)
  || getEnvVar('NODE_ENV', 'development');

// 导出当前环境的端口配置，确保总是有有效的配置
export const PORTS = portConfigs[env] || portConfigs["development"];

// 生成完整的URL配置
// 根据环境使用不同的域名
const getBaseUrl = (serviceName: string, port: number): string => {
  if (env === 'production') {
    // 生产环境使用实际域名
    const domainMap: Record<string, string> = {
      msAdmin: 'https://admin.ihoms.cn',
      // 登录中心独立域名（多域名模式）
      msLogin: 'https://login.ihoms.cn',
      // space 独立域名（若你们仍使用 /space 反代，可改回 https://www.ihoms.cn/space）
      msSpace: 'https://space.ihoms.cn',
      msStun: 'https://stun.ihoms.cn',
      msMq: 'https://mq.ihoms.cn',
      msLog: 'https://log.ihoms.cn',
      // 主站建议统一用 ihoms.cn（如实际仍用 www，可改回）
      msMain: 'https://ihoms.cn',
    };
    return domainMap[serviceName] || `http://localhost:${port}`;
  }
  // 开发环境使用 localhost
  return `http://localhost:${port}`;
};

export const URLS = {
  // 前端URL
  msAdmin: getBaseUrl('msAdmin', PORTS.msAdmin),
  msLogin: getBaseUrl('msLogin', PORTS.msLogin),
  msSpace: getBaseUrl('msSpace', PORTS.msSpace),
  msStun: getBaseUrl('msStun', PORTS.msStun),
  msMq: getBaseUrl('msMq', PORTS.msMq),
  msLog: getBaseUrl('msLog', PORTS.msLog),
  msMain: getBaseUrl('msMain', PORTS.msMain),

  // 后端API URL
  // 生产环境：各服务各自域名。msAdmin 实际由 Nginx /api 反代到 msLogin 后端（同源请求可避免 CORS）。
  msAdminApi: env === 'production' ? 'https://admin.ihoms.cn' : `http://localhost:${PORTS.msAdminApi}`,
  msLoginApi: env === 'production' ? 'https://login.ihoms.cn' : `http://localhost:${PORTS.msLoginApi}`,
  msSpaceApi: env === 'production' ? 'https://space.ihoms.cn' : `http://localhost:${PORTS.msSpaceApi}`,
  msStunApi: env === 'production' ? 'https://stun.ihoms.cn' : `http://localhost:${PORTS.msStunApi}`,
  msMqApi: env === 'production' ? 'https://mq.ihoms.cn' : `http://localhost:${PORTS.msMqApi}`,
  msLogApi: env === 'production' ? 'https://log.ihoms.cn' : `http://localhost:${PORTS.msLogApi}`,
};

// 生成带API前缀的URL
export const API_URLS = {
  msAdminApi: `${URLS.msAdminApi}/api`,
  msLoginApi: `${URLS.msLoginApi}/api`,
  msSpaceApi: `${URLS.msSpaceApi}/api`,
  msStunApi: `${URLS.msStunApi}/api`,
  msMqApi: `${URLS.msMqApi}/api`,
  msLogApi: `${URLS.msLogApi}/api`,
};

// 统一的后端API地址（当前使用msLogin作为统一后端）
export const UNIFIED_API_URL = URLS.msLoginApi; // http://localhost:50090 (不包含 /api)
export const UNIFIED_API_BASE = `${UNIFIED_API_URL}/api`; // http://localhost:50090/api

/**
 * 角色 CRUD、角色权限等由 **msLogin** 提供（/api/role/*）。
 * 若 REACT_APP_API_URL 指向 admin 等未反代这些路由的域名，会 404；角色相关请求应使用本函数返回的 base。
 * 生产环境 msLogin 若在 login 子域，可设 REACT_APP_MSLOGIN_API_URL=https://login.ihoms.cn
 */
export function getMsLoginApiBase(): string {
  const raw = getEnvVar("REACT_APP_MSLOGIN_API_URL", "").trim();
  if (raw) {
    const u = raw.replace(/\/$/, "");
    return u.endsWith("/api") ? u : `${u}/api`;
  }
  return UNIFIED_API_BASE;
}

// 常用配置
export const COMMON_CONFIG = {
  // 当前服务配置
  CURRENT_SERVICE: {
    name: 'msAdmin',
    frontendPort: PORTS.msAdmin,
    frontendUrl: URLS.msAdmin,
    apiPort: PORTS.msLoginApi, // 使用统一后端
    apiUrl: UNIFIED_API_BASE,
  },

  // 跨域配置
  CORS_ORIGINS: [
    URLS.msMain,
    URLS.msLogin,
    URLS.msAdmin,
    URLS.msSpace,
    URLS.msStun,
    URLS.msMq,
    URLS.msLog,
  ],

  // iframe安全配置
  FRAME_ANCESTORS: [
    URLS.msMain,
    URLS.msLogin,
    URLS.msAdmin,
  ].join(' '),
};

// ===== 环境配置 =====

// 基础配置接口
interface ApiConfig {
  BASE_URL: string;
  API_PREFIX: string;
  TIMEOUT: number;
  RETRY_COUNT: number;
  RETRY_DELAY: number;
}

// 不同环境的配置
const configs: Record<string, ApiConfig> = {
  development: {
    BASE_URL:
      getEnvVar('REACT_APP_API_URL') ||
      (() => {
        console.warn("⚠️ REACT_APP_API_URL 未配置，使用统一后端");
        return UNIFIED_API_BASE; // 使用统一的后端URL，包含/api前缀
      })(),
    API_PREFIX: "", // 因为BASE_URL已经包含/api前缀，所以这里设为空
    TIMEOUT: parseInt(getEnvVar('REACT_APP_TIMEOUT', '15000')),
    RETRY_COUNT: 1,
    RETRY_DELAY: 1000,
  },
  test: {
    BASE_URL: "http://test-api.example.com:50090",
    API_PREFIX: "/api",
    TIMEOUT: 15000,
    RETRY_COUNT: 1,
    RETRY_DELAY: 1000,
  },
  production: {
    BASE_URL: (() => {
      // 生产环境使用 www.ihoms.cn 作为统一后端 API 地址
      // 优先使用环境变量 REACT_APP_API_URL，如果没有则使用默认值
      const url = getEnvVar('REACT_APP_API_URL', 'https://www.ihoms.cn/api');
      // 确保 URL 包含 /api 前缀
      const finalUrl = url.includes('/api') ? url : `${url}/api`;
      return finalUrl;
    })(),
    API_PREFIX: "", // 因为 BASE_URL 已经包含 /api 前缀，所以这里设为空
    TIMEOUT: 20000,
    RETRY_COUNT: 0, // 生产环境不自动重试
    RETRY_DELAY: 0,
  },
};

// 当前环境的配置，确保总是有有效的配置
export const API_CONFIG = configs[env] || configs["development"];

// 其他网络相关的通用配置
export const NET_CONFIG = {
  // 分页配置
  PAGINATION: {
    DEFAULT_PAGE_SIZE: 10,
    DEFAULT_CURRENT_PAGE: 1,
  },

  // 缓存配置
  CACHE: {
    ENABLED: true,
    TTL: 60000, // 缓存有效期，毫秒
    STORAGE_KEY_PREFIX: "api_cache_",
  },

  // 调试选项
  DEBUG: {
    ENABLED: getEnvVar('REACT_APP_DEBUG', 'false') === "true",
    LOG_REQUESTS: true,
    LOG_RESPONSES: true,
  },
};

// ===== Axios实例配置 =====

// 请求计数器与加载状态
let requestCount = 0;
const loadingDelay = 300;
let loadingTimer: ReturnType<typeof setTimeout> | null = null;

type CreateApiClientOptions = {
  /** 若提供，每次请求前覆盖 config.baseURL（用于 msLogin 等独立 API 根，避免与默认实例合并异常） */
  baseURLResolver?: () => string;
};

// 创建axios实例
export const createApiClient = (options?: CreateApiClientOptions): AxiosInstance => {
  const baseURLResolver = options?.baseURLResolver;
  // 合并baseURL和前缀
  const baseURL = `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}`;

  const instance = axios.create({
    baseURL,
    timeout: API_CONFIG.TIMEOUT,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // 请求拦截器
  instance.interceptors.request.use(
    (config) => {
      if (baseURLResolver) {
        config.baseURL = baseURLResolver().replace(/\/$/, "");
      }

      // 增加请求计数
      requestCount++;

      // 处理全局loading
      if (requestCount === 1 && !loadingTimer) {
        loadingTimer = setTimeout(() => {
          // 这里可以触发全局loading状态
          // 例如使用Redux: dispatch(setLoading(true))
        }, loadingDelay);
      }

      // 添加认证信息 (优先使用mslogin_token)
      const token =
        localStorage.getItem("mslogin_token") || localStorage.getItem("token") || localStorage.getItem("admin_token");
      if (token) {
        if (!config.headers) {
          config.headers = new AxiosHeaders();
        }
        config.headers["Authorization"] = `Bearer ${token}`;
      }

      // 调试日志
      if (NET_CONFIG.DEBUG.ENABLED && NET_CONFIG.DEBUG.LOG_REQUESTS) {
      }

      return config;
    },
    (error) => {
      handleRequestComplete();
      return Promise.reject(error);
    }
  );

  // 响应拦截器
  instance.interceptors.response.use(
    (response) => {
      handleRequestComplete();

      // 调试日志
      if (NET_CONFIG.DEBUG.ENABLED && NET_CONFIG.DEBUG.LOG_RESPONSES) {
      }

      return response;
    },
    async (error: AxiosError) => {
      handleRequestComplete();

      // 调试日志
      if (NET_CONFIG.DEBUG.ENABLED) {
      }

      // 处理重试逻辑
      const config = error.config as AxiosRequestConfig & { _retryCount?: number };

      if (config) {
        config._retryCount = config._retryCount || 0;

        // 如果可以重试且未超出重试次数
        if (shouldRetry(error) && config._retryCount < API_CONFIG.RETRY_COUNT) {
          config._retryCount += 1;

          // 延迟重试
          await new Promise((resolve) => setTimeout(resolve, API_CONFIG.RETRY_DELAY));

          return instance(config);
        }
      }

      // 统一错误处理
      handleApiError(error);

      return Promise.reject(error);
    }
  );

  return instance;
};

// 判断是否应该重试请求
function shouldRetry(error: AxiosError): boolean {
  // 网络错误应该重试
  if (error.message && error.message.includes("Network Error")) {
    return true;
  }

  // 服务器错误(5xx)应该重试
  if (error.response && error.response.status >= 500 && error.response.status < 600) {
    return true;
  }

  // 请求超时应该重试
  if (error.code === "ECONNABORTED") {
    return true;
  }

  return false;
}

// 处理请求完成
function handleRequestComplete() {
  requestCount--;

  if (requestCount <= 0) {
    requestCount = 0;
    if (loadingTimer) {
      clearTimeout(loadingTimer);
      loadingTimer = null;
    }
    // 关闭全局loading
    // 例如使用Redux: dispatch(setLoading(false))
  }
}

// 处理API错误
function handleApiError(error: AxiosError) {
  if (error.response) {
    const status = error.response.status;
    const errorData = error.response.data as any;

    switch (status) {
      case 401:
        // 完全禁用调试信息输出
        // console.log('[msAdmin] 收到401错误，开始处理认证失败');

        // 在iframe环境下，通知父应用处理认证失败
        if (window.parent !== window) {
          // 完全禁用调试信息输出
          // console.log('[msAdmin] iframe环境下的401错误，通知父应用处理');
          // 通知msLogin尝试刷新Token
          notifyAuthFailed();

          // 显示友好的提示信息
          message.warning("认证已过期，正在尝试自动刷新...");
        } else {
          // 独立环境下的处理 - 不跳转页面，只清除token
          message.error("用户未登录或登录已过期，请重新登录");
          localStorage.removeItem("admin_token");
          sessionStorage.removeItem("admin_token");
          localStorage.removeItem("mslogin_token");
          localStorage.removeItem("mslogin_user");
          localStorage.removeItem("token");

          // 不自动跳转页面，让用户手动操作
          // if (!window.location.pathname.includes('/login')) {
          //   setTimeout(() => window.location.href = '/login', 1500);
          // }
        }
        break;

      case 403:
        message.error("您没有权限执行此操作");
        break;

      case 404:
        message.error(
          (errorData?.message && String(errorData.message)) || "请求的资源不存在",
        );
        break;

      case 422:
        // 表单验证错误
        if (errorData && errorData.errors) {
          const firstError = Object.values(errorData.errors)[0];
          message.error(Array.isArray(firstError) ? firstError[0] : String(firstError));
        } else {
          message.error("提交的数据无效");
        }
        break;

      case 429:
        message.error("请求过于频繁，请稍后再试");
        break;

      case 500:
        message.error("服务器内部错误，请稍后再试");
        break;

      default:
        message.error(errorData?.message || `请求失败(${status})：${error.response.statusText || "未知错误"}`);
    }
  } else if (error.request) {
    // 请求发出但没有收到响应
    message.error("无法连接到服务器，请检查网络连接");
  } else {
    // 请求配置出错
    message.error(`请求配置错误: ${error.message}`);
  }
}

// ===== API实例与工具函数 =====

// 创建默认API实例
export const apiClient = createApiClient();

/** 固定走 msLogin /api（见 getMsLoginApiBase）；角色/角色权限等请用本实例，勿用 apiClient + 单次 baseURL */
export const msLoginApiClient = createApiClient({ baseURLResolver: getMsLoginApiBase });

// API初始化函数
export function setupNetConfig() {
  // 可以在此添加其他初始化逻辑
}

export const api = createApiClient();

// 开发环境调试信息
if (env === 'development') {
  console.log('🔧 网络配置加载完成:', {
    environment: env,
    ports: PORTS,
    unifiedApiUrl: UNIFIED_API_BASE,
    baseUrl: `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}`,
  });
}

export default {
  setup: setupNetConfig,
  apiClient: api,
  apiConfig: API_CONFIG,
  config: NET_CONFIG, // 改为 config 而不是 netConfig
  createApiClient,
  baseUrl: `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}`,
  // 端口配置相关导出
  PORTS,
  URLS,
  API_URLS,
  UNIFIED_API_URL,
  UNIFIED_API_BASE,
  COMMON_CONFIG,
} as const;
